import type { QRCodeErrorCorrectionLevel } from 'qrcode';
import { jsPDF } from 'jspdf';

export type QRType = 'url' | 'text' | 'wifi' | 'email' | 'phone' | 'sms' | 'vcard';

export interface QRFormData {
  url?: string;
  text?: string;
  ssid?: string;
  password?: string;
  encryption?: string;
  hidden?: boolean;
  email?: string;
  subject?: string;
  body?: string;
  phone?: string;
  message?: string;
  name?: string;
  org?: string;
  title?: string;
  url_vcard?: string;
  address?: string;
}

export interface QROptions {
  fgColor: string;
  bgColor: string;
  level: QRCodeErrorCorrectionLevel;
  margin: number;
  logo?: string | null;
  logoWhiteBorder: boolean;
  logoBorderSize: number;
}

export const COLOR_PRESETS = [
  { name: 'Classic', fg: '#0F172A', bg: '#FFFFFF' },
  { name: 'Brand Blue', fg: '#6366F1', bg: '#FFFFFF' },
  { name: 'Violet', fg: '#8B5CF6', bg: '#FFFFFF' },
  { name: 'Warm', fg: '#F59E0B', bg: '#FFFFFF' },
  { name: 'Red', fg: '#EF4444', bg: '#FFFFFF' },
  { name: 'Green', fg: '#22C55E', bg: '#FFFFFF' },
  { name: 'Dark', fg: '#FFFFFF', bg: '#0F172A' },
  { name: 'Slate', fg: '#0F172A', bg: '#F8FAFC' },
];

export function generateQRContent(type: QRType, data: QRFormData): string {
  switch (type) {
    case 'url':
      return data.url?.trim() || '';
    case 'text':
      return data.text?.trim() || '';
    case 'wifi': {
      const ssid = escapeWifiValue(data.ssid || '');
      const pass = escapeWifiValue(data.password || '');
      const enc = data.encryption || 'WPA';
      const hidden = data.hidden ? 'true' : 'false';
      return `WIFI:T:${enc};S:${ssid};P:${pass};H:${hidden};;`;
    }
    case 'email': {
      const e = data.email?.trim() || '';
      const sub = encodeURIComponent(data.subject || '');
      const b = encodeURIComponent(data.body || '');
      if (!e) return '';
      return `mailto:${e}?subject=${sub}&body=${b}`;
    }
    case 'phone':
      return data.phone?.trim() ? `tel:${data.phone.trim()}` : '';
    case 'sms': {
      const p = data.phone?.trim() || '';
      const m = data.message?.trim() || '';
      if (!p) return '';
      return `SMSTO:${p}:${m}`;
    }
    case 'vcard': {
      const lines: string[] = ['BEGIN:VCARD', 'VERSION:3.0'];
      if (data.name) lines.push(`FN:${data.name}`);
      if (data.phone) lines.push(`TEL:${data.phone}`);
      if (data.email) lines.push(`EMAIL:${data.email}`);
      if (data.org) lines.push(`ORG:${data.org}`);
      if (data.title) lines.push(`TITLE:${data.title}`);
      if (data.url_vcard) lines.push(`URL:${data.url_vcard}`);
      if (data.address) lines.push(`ADR:;;${data.address}`);
      lines.push('END:VCARD');
      return lines.join('\n');
    }
    default:
      return '';
  }
}

function escapeWifiValue(str: string): string {
  return str.replace(/[\\;,:\\]/g, '\\$&');
}

export function isValidUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function getDefaultFormData(type: QRType): QRFormData {
  switch (type) {
    case 'url':
      return { url: '' };
    case 'text':
      return { text: '' };
    case 'wifi':
      return { ssid: '', password: '', encryption: 'WPA', hidden: false };
    case 'email':
      return { email: '', subject: '', body: '' };
    case 'phone':
      return { phone: '' };
    case 'sms':
      return { phone: '', message: '' };
    case 'vcard':
      return { name: '', phone: '', email: '', org: '', title: '', url_vcard: '', address: '' };
    default:
      return {};
  }
}

export function getDefaultOptions(): QROptions {
  return {
    fgColor: '#0F172A',
    bgColor: '#FFFFFF',
    level: 'M',
    margin: 2,
    logo: null,
    logoWhiteBorder: true,
    logoBorderSize: 8,
  };
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function downloadSvg(svgString: string, filename: string) {
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadPngAtSize(
  qrDataUrl: string,
  size: number,
  filename: string,
  logoUrl?: string | null,
  options?: QROptions
) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Fill background
  ctx.fillStyle = options?.bgColor || '#FFFFFF';
  ctx.fillRect(0, 0, size, size);

  // Draw QR code
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject();
    img.src = qrDataUrl;
  });
  ctx.drawImage(img, 0, 0, size, size);

  // Overlay logo if present
  if (logoUrl) {
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      logoImg.onload = () => resolve();
      logoImg.onerror = () => reject();
      logoImg.src = logoUrl;
    });

    const maxLogoSize = Math.round(size * 0.3);
    const logoSize = Math.min(maxLogoSize, logoImg.width, logoImg.height);
    const x = (size - logoSize) / 2;
    const y = (size - logoSize) / 2;

    if (options?.logoWhiteBorder) {
      const border = options.logoBorderSize * (size / 1024);
      ctx.save();
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      const r = Math.round(border);
      const rx = x - r;
      const ry = y - r;
      const rw = logoSize + r * 2;
      const rh = logoSize + r * 2;
      roundRect(ctx, rx, ry, rw, rh, r);
      ctx.fill();
      ctx.restore();
    }

    ctx.drawImage(logoImg, x, y, logoSize, logoSize);
  }

  const outDataUrl = canvas.toDataURL('image/png');
  downloadDataUrl(outDataUrl, filename);
}

export async function downloadPdf(
  qrDataUrl: string,
  filename: string,
  typeLabel: string,
  logoUrl?: string | null,
  options?: QROptions
) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210;
  // A4 height 297mm

  // Title
  doc.setFontSize(18);
  doc.setTextColor(15, 23, 42);
  doc.text('QR Code', pageW / 2, 20, { align: 'center' });

  doc.setFontSize(12);
  doc.setTextColor(100, 116, 139);
  doc.text(typeLabel, pageW / 2, 28, { align: 'center' });

  // QR image area
  const qrSize = 120;
  const qrX = (pageW - qrSize) / 2;
  const qrY = 45;

  // Create high-res canvas for PDF
  const canvas = document.createElement('canvas');
  const pxSize = 1024;
  canvas.width = pxSize;
  canvas.height = pxSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = options?.bgColor || '#FFFFFF';
  ctx.fillRect(0, 0, pxSize, pxSize);

  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject();
    img.src = qrDataUrl;
  });
  ctx.drawImage(img, 0, 0, pxSize, pxSize);

  if (logoUrl) {
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      logoImg.onload = () => resolve();
      logoImg.onerror = () => reject();
      logoImg.src = logoUrl;
    });
    const maxLogoSize = Math.round(pxSize * 0.3);
    const logoSize = Math.min(maxLogoSize, logoImg.width, logoImg.height);
    const x = (pxSize - logoSize) / 2;
    const y = (pxSize - logoSize) / 2;

    if (options?.logoWhiteBorder) {
      const border = options.logoBorderSize * (pxSize / 1024);
      ctx.save();
      ctx.fillStyle = '#FFFFFF';
      const r = Math.round(border);
      const rx = x - r;
      const ry = y - r;
      const rw = logoSize + r * 2;
      const rh = logoSize + r * 2;
      roundRect(ctx, rx, ry, rw, rh, r);
      ctx.fill();
      ctx.restore();
    }
    ctx.drawImage(logoImg, x, y, logoSize, logoSize);
  }

  const pngData = canvas.toDataURL('image/png');
  doc.addImage(pngData, 'PNG', qrX, qrY, qrSize, qrSize);

  // Footer note
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text('Generated by CikaStudio · cika.studio', pageW / 2, qrY + qrSize + 10, { align: 'center' });

  doc.save(filename);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

export function formatDateForFilename(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}
